import { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { checkTypeStringRegexDatagrid, displayErrorMessage } from 'utils/tools_functions';
import Button from '@mui/material/Button';
import { inputRequired } from 'components/generics/Validators/validator';
import { validateFormData } from 'components/generics/Validators/form.validate';
import { ThemeProvider } from '@mui/material/styles';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  ListItemText,
  FormControl,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { customButtonTheme } from 'assets/styles/themes/generic_button_mui';
import { StyledTooltipErrorInputCostModal } from 'utils/Datagrid/CustomCultureDataGridUtils';
import { customDialogAddEditCommonTheme } from 'assets/styles/themes/Dialog/generic_dialog_add_edit_parcel';
import { IMPORT_TYPE } from 'utils/GlobalVariables';
import { addGroundTypeMappingData } from 'services/API/Cooperative';

const AddGroundTypeMappingDataModal = ({
  isModalCreateOpen,
  setModalCreateOpen,
  groundTypeList,
  cooperativeId,
  mappingTypes,
  selectedMappingType,
  actions,
  refreshDataAfterNewFileImport,
  groundTypeMappingData,
}) => {
  const { t } = useTranslation();
  const DEFAULT_FORM = {
    externalName: '',
    groundType: '',
    mappingType: selectedMappingType ?? mappingTypes?.[0] ?? IMPORT_TYPE.geofolia,
  };
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [errorsFormDefault, setErrorsFormDefault] = useState({
    formError: false,
    externalName: {
      message: '',
      validator: [inputRequired],
    },
    groundType: {
      message: '',
      validator: [inputRequired],
    },
  });
  const isOpen = useMemo(() => errorsFormDefault.formError, [errorsFormDefault]);

  const updateForm = (key, value) => {
    setFormData({ ...formData, [key]: value });
  };

  const onSubmit = async (e) => {
    errorsFormDefault.formError = false;
    const payload = {
      ...formData,
      groundType: formData.groundType === '' ? null : formData.groundType,
    };
    setErrorsFormDefault({ ...validateFormData(payload, errorsFormDefault) });
    if (!errorsFormDefault.formError) {
      try {
        setModalCreateOpen(false);

        const response = await addGroundTypeMappingData(
          formData.mappingType,
          cooperativeId,
          payload,
        );
        const newMappingData = [
          ...groundTypeMappingData?.[formData.mappingType],
          response?.data?.data,
        ];
        refreshDataAfterNewFileImport(formData.mappingType, newMappingData);
        toast.success(t('success.mappingGroundTypeCreated', { name: formData.externalName }));
        setFormData(DEFAULT_FORM);
      } catch (error) {
        toast.error(error?.response?.data?.message ?? displayErrorMessage('ERR_UNEXPECTED'));
      }
    }
  };

  return (
    <ThemeProvider theme={customDialogAddEditCommonTheme}>
      <Dialog
        id="custom-addParcel-dialog"
        maxWidth="md"
        fullWidth
        open={isModalCreateOpen}
        onClose={() => {
          setModal(false);
        }}
      >
        <Box className="modal_header">
          <DialogTitle>{t('title.addMappingGroundType')}</DialogTitle>
          <ClearIcon className="close-icon" onClick={() => setModalCreateOpen(false)} />
        </Box>
        <DialogContent>
          <Box>
            <InputLabel id="nameLabel" htmlFor="externalName">
              {t('label.externalName')}
            </InputLabel>
            {errorsFormDefault?.externalName.message && (
              <div>
                <StyledTooltipErrorInputCostModal
                  open={isOpen}
                  title={errorsFormDefault?.externalName.message}
                  placement="top"
                >
                  <span></span>
                </StyledTooltipErrorInputCostModal>
              </div>
            )}
            <TextField
              id="externalName"
              value={formData.externalName}
              onChange={(e) => {
                updateForm('externalName', checkTypeStringRegexDatagrid(e.target.value));
              }}
              placeholder={t('placeholder.enterName')}
              className={errorsFormDefault?.externalName.message ? 'MuiErrorForm' : ''}
            />
          </Box>

          <Box>
            <InputLabel id="GroundTypeAssoliaLabel" htmlFor="groundType">
              {t('label.groundType')}
            </InputLabel>
            {errorsFormDefault?.groundType.message && (
              <div>
                <StyledTooltipErrorInputCostModal
                  open={isOpen}
                  title={errorsFormDefault?.groundType.message}
                  placement="top"
                >
                  <span></span>
                </StyledTooltipErrorInputCostModal>
              </div>
            )}
            <FormControl>
              <Select
                displayEmpty
                id="groundType"
                onChange={(e) => updateForm('groundType', e.target.value)}
                value={formData.groundType}
                renderValue={(value) => {
                  if (value === '') {
                    return t('message.noGroundTypeAssociated');
                  }
                  const selectedGroundType = groundTypeList.find(
                    (groundType) => groundType.value === value,
                  );
                  return selectedGroundType ? selectedGroundType.label : value;
                }}
              >
                {groundTypeList?.map((groundType) => (
                  <MenuItem key={groundType?.value} value={groundType?.value}>
                    <ListItemText primary={groundType?.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <InputLabel id="mappingTypeLabel" htmlFor="mappingType">
              {t('label.mappingType')}
            </InputLabel>
            <FormControl>
              <Select
                id="mappingType"
                onChange={(e) => updateForm('mappingType', e.target.value)}
                value={formData.mappingType}
              >
                {actions?.map((action) => (
                  <MenuItem key={action?.name} value={action?.name}>
                    <ListItemText primary={action?.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <ThemeProvider theme={customButtonTheme}>
            <Button id="addGroundTypeMappingButton" onClick={onSubmit}>
              {t('common.add')}
            </Button>
          </ThemeProvider>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default AddGroundTypeMappingDataModal;
